import type { Document } from "../document/Document";
import type { GroupLayer, Layer } from "../document/Layer";
import type { Command } from "./Command";
import type { ActionStep } from "../actions/ActionStep";
import { DeleteLayerCommand } from "./DeleteLayerCommand";

// Insert a new layer into the tree.
//
// - `parentGroupId` selects the destination group; null/undefined = root.
// - `insertAbove` is the id of the existing sibling the new layer should sit
//   above. null/undefined means "place on top of the destination list"
//   (remember: layers are bottom-first, so "top" is the end of the array).
//
// Invert is a DeleteLayerCommand for the same id.

export class NewLayerCommand implements Command {
  readonly kind = "new-layer";
  constructor(
    private readonly layer: Layer,
    private readonly parentGroupId?: string,
    private readonly insertAbove?: string,
  ) {}

  apply(doc: Document): Document {
    return { ...doc, layers: insert(doc.layers, this.layer, this.parentGroupId, this.insertAbove), activeLayerId: this.layer.id };
  }

  invert(_before: Document, _after: Document): Command {
    return new DeleteLayerCommand(this.layer.id);
  }

  toActionStep(): ActionStep {
    return {
      id: crypto.randomUUID(),
      name: "New Layer",
      command: "new-layer",
      params: { kind: this.layer.kind },
      recordable: true,
    };
  }
}

function insert(
  layers: readonly Layer[],
  layer: Layer,
  parentId: string | undefined,
  insertAbove: string | undefined,
): Layer[] {
  if (!parentId) {
    return insertIntoList(layers, layer, insertAbove);
  }
  return layers.map((l) => {
    if (l.id === parentId && l.kind === "group") {
      const group: GroupLayer = { ...l, children: insertIntoList(l.children, layer, insertAbove) };
      return group;
    }
    if (l.kind === "group") {
      return { ...l, children: insert(l.children, layer, parentId, insertAbove) };
    }
    return l;
  });
}

function insertIntoList(
  list: readonly Layer[],
  layer: Layer,
  insertAbove: string | undefined,
): Layer[] {
  if (!insertAbove) {
    return [...list, layer];
  }
  const idx = list.findIndex((l) => l.id === insertAbove);
  if (idx < 0) return [...list, layer];
  // "Above" in the UI = visually higher = greater index in our bottom-first list.
  return [...list.slice(0, idx + 1), layer, ...list.slice(idx + 1)];
}

